import { useEffect } from 'react'

// The viewport shrinking by less than this is a browser toolbar showing, not a keyboard
const MIN_KEYBOARD_HEIGHT = 100
// Moving the Continue button up by less than this isn't worth reshaping it
const MIN_COVERED_HEIGHT = 48

/**
 * Sets `data-keyboard-open` and `--keyboard-inset` (how much of the layout viewport the keyboard covers) on <html>,
 * and `data-keyboard-covered` on the focused step's <footer> when the keyboard hides its on-page spot.
 * CSS reads them instead of React state, so viewport events don't re-render the steps.
 */
const useKeyboardInset = (enabled: boolean): void => {
  useEffect(() => {
    const viewport = window.visualViewport
    if (!enabled || viewport == null) return

    const root = document.documentElement
    let width = viewport.width
    let fullHeight = viewport.height
    let frame = 0
    let footer: HTMLElement | undefined
    let footerHeight = 0

    const update = () => {
      frame = 0
      if (viewport.width !== width) {
        width = viewport.width
        fullHeight = viewport.height
      }
      fullHeight = Math.max(fullHeight, viewport.height)

      const focused = document.activeElement
      const isTyping = focused instanceof HTMLInputElement || focused instanceof HTMLTextAreaElement
      // The keyboard either overlays the page (iOS, Android Chrome) or shrinks the whole window (some Android browsers).
      // Both shrink the visual viewport, but only an overlaying keyboard covers part of the layout viewport.
      const isOpen = isTyping && fullHeight - viewport.height > MIN_KEYBOARD_HEIGHT
      const inset = isOpen ? Math.max(0, root.clientHeight - viewport.height - viewport.offsetTop) : 0

      root.toggleAttribute('data-keyboard-open', isOpen)
      root.style.setProperty('--keyboard-inset', `${inset}px`)

      // Kept while the keyboard closes, so the footer can transition back
      const stepFooter = isTyping ? [...document.querySelectorAll('footer')].find((f) => f.parentElement?.contains(focused)) : undefined
      if (stepFooter != null && stepFooter !== footer) {
        footer = stepFooter
        // The content above the footer can grow (e.g. the BMI card) without any viewport event
        resizeObserver.disconnect()
        if (footer.parentElement != null) resizeObserver.observe(footer.parentElement)
      }
      if (footer?.previousElementSibling == null) return

      // Measured in its regular shape only, so reshaping it can't flip the result back
      if (!footer.hasAttribute('data-keyboard-covered')) footerHeight = footer.offsetHeight
      // In the flex column, the footer's on-page spot is right below the content before it
      const covered = footer.previousElementSibling.getBoundingClientRect().bottom + footerHeight - (root.clientHeight - inset)
      footer.toggleAttribute('data-keyboard-covered', isOpen && covered > MIN_COVERED_HEIGHT)
    }

    const scheduleUpdate = () => {
      if (frame === 0) frame = requestAnimationFrame(update)
    }
    const resizeObserver = new ResizeObserver(scheduleUpdate)

    viewport.addEventListener('resize', scheduleUpdate)
    viewport.addEventListener('scroll', scheduleUpdate)
    document.addEventListener('focusin', scheduleUpdate)
    document.addEventListener('focusout', scheduleUpdate)
    window.addEventListener('scroll', scheduleUpdate)
    update()

    return () => {
      cancelAnimationFrame(frame)
      viewport.removeEventListener('resize', scheduleUpdate)
      viewport.removeEventListener('scroll', scheduleUpdate)
      document.removeEventListener('focusin', scheduleUpdate)
      document.removeEventListener('focusout', scheduleUpdate)
      window.removeEventListener('scroll', scheduleUpdate)
      resizeObserver.disconnect()
      footer?.removeAttribute('data-keyboard-covered')
      root.removeAttribute('data-keyboard-open')
      root.style.removeProperty('--keyboard-inset')
    }
  }, [enabled])
}

export default useKeyboardInset
